import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Glossary, Term, Steps, Step } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { ChordBuilder } from "@/components/mt/ChordBuilder";
import { NextUp } from "@/components/Callouts";

export default function MusicTheory21() {
  const { lesson, course, accent } = findLesson("music-theory", "21");
  return (
    <LessonShell
      course={course}
      courseSlug="music-theory"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/music-theory/MT21-Seventh-Chords-Animated.mp4"
        caption="7th chords — Cmaj7 / C7 / Cm7 / Cm7♭5 / C°7"
        hint="≈ 32 sec · watch first"
      />
      <Chapter number="21" eyebrow="Music Theory · Chapter Twenty-one" title="Add a fourth note — and the chord grows up">
        <Prose>
          <p>
            We've stacked two thirds to make a triad. Stack <strong>one more third</strong>{" "}
            on top — four notes now — and we arrive at the <strong>seventh chord</strong>.
            One extra note. A whole new emotional vocabulary.
          </p>
          <p>
            Seventh chords are the harmonic engine of jazz, soul, blues, gospel, R&B and
            most "grown-up" pop. The major 7th sound underneath a Norah Jones tune; the
            dominant 7 in every blues twelve-bar; the minor 7 chords floating through Steely
            Dan and Bill Withers — all four-note chords, all built from the same idea:
            triad + 7th.
          </p>
          <MTQuote>
            Let's now move forward beyond triads and see what more than three-note chords
            have to offer. We call them seventh chords, we call them add chords, we call
            them extensions.
          </MTQuote>
        </Prose>
        <NoteImage
          src="/notes/music-theory/finals/21a-major-7th-chord-formula.png"
          caption="Cmaj7 = 1 3 5 7 = C E G B = the major triad with the major 7th added on top. Every 7th chord in jazz is built this way: take a triad, then stack one more interval (M7 or m7) above it."
        />
      </Chapter>

      <Chapter eyebrow="The five seventh chords" title="Five flavours · one stacking principle">
        <Prose>
          <p>
            There are five seventh chords you absolutely have to know. They cover almost
            every chart you'll ever read. Each one combines a triad type with one of two
            kinds of seventh:
          </p>
          <ul className="space-y-1 text-[14px]">
            <li>The <strong>major 7th</strong> — a major 3rd above the 5th. From C, that's <strong>B</strong>. (11 semitones above the root.)</li>
            <li>The <strong>minor 7th</strong> — a minor 3rd above the 5th. From C, that's <strong>B♭</strong>. (10 semitones above the root.)</li>
            <li>The <strong>diminished 7th</strong> — a minor 3rd above the diminished 5th. From C, that's <strong>B♭♭</strong> (enharmonically A). (9 semitones above the root.)</li>
          </ul>
        </Prose>

        <Steps>
          <Step><strong>Major 7 (maj7)</strong> — major triad + major 7. Formula: 1 · 3 · 5 · 7. (Cmaj7 = C E G B.) Lush, cinematic, "open" — the sound of a romantic comedy ending.</Step>
          <Step><strong>Dominant 7 (7)</strong> — major triad + minor 7. Formula: 1 · 3 · 5 · ♭7. (C7 = C E G B♭.) The blues chord. Tense, unresolved — wants to fall a fifth.</Step>
          <Step><strong>Minor 7 (m7)</strong> — minor triad + minor 7. Formula: 1 · ♭3 · 5 · ♭7. (Cm7 = C E♭ G B♭.) Smooth, soulful, the sound of every neo-soul groove.</Step>
          <Step><strong>Half-diminished (m7♭5)</strong> — diminished triad + minor 7. Formula: 1 · ♭3 · ♭5 · ♭7. (Cm7♭5 = C E♭ G♭ B♭.) The "ii" of a minor key — wistful and unstable.</Step>
          <Step><strong>Fully diminished (dim7)</strong> — diminished triad + diminished 7. Formula: 1 · ♭3 · ♭5 · ♭♭7. (Cdim7 = C E♭ G♭ B♭♭, that last note sounding as A.) Maximum tension; a stack of pure minor 3rds.</Step>
        </Steps>

        <TheoryBox label="The seventh-chord stack — at a glance">
          <div className="text-[14px] leading-relaxed">
            <strong>maj7</strong> = 1 · 3 · 5 · 7 &nbsp; (C E G B) <br />
            <strong>7</strong>      = 1 · 3 · 5 · ♭7 &nbsp;(C E G B♭) <br />
            <strong>m7</strong>     = 1 · ♭3 · 5 · ♭7 &nbsp;(C E♭ G B♭) <br />
            <strong>m7♭5</strong>   = 1 · ♭3 · ♭5 · ♭7 &nbsp;(C E♭ G♭ B♭) <br />
            <strong>dim7</strong>   = 1 · ♭3 · ♭5 · ♭♭7 &nbsp;(C E♭ G♭ B♭♭)
          </div>
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="The character of each seventh" title="Why the same root sounds five different ways">
        <Prose>
          <p>
            Cmaj7, C7, Cm7, Cm7♭5 and Cdim7 all share the same root note — C. But they paint
            five completely different emotional landscapes. That's because the seventh you
            stack on top reaches up into a new harmonic space and shifts how the whole chord
            "wants" to behave.
          </p>
          <p>
            <strong>Maj7</strong> sits there. It's at rest. Add a maj7 to a major triad and
            you get the most relaxed, satisfied chord in tonal music — the sound of arrival.
            Songs end on maj7 chords for a reason.
          </p>
          <p>
            <strong>Dom7</strong> won't sit. The interval between its 3 and ♭7 (E to B♭) is a
            tritone — three whole-tones, the most unstable interval in the system. A
            dominant 7 chord is always pulling somewhere, usually down a perfect fifth.
            G7 → C is the cadence that built classical music, the blues, jazz, and rock.
          </p>
          <p>
            <strong>Minor 7</strong> is the chord of cool. Smooth and slightly sad without
            being heavy. It's "minor without the drama". Because it's so stable, it lives
            for bars at a time in jazz tunes — D-7 in <em>So What</em>, F-7 in countless
            soul records.
          </p>
          <p>
            <strong>Half-diminished (m7♭5)</strong> is the most poetic chord on this list.
            It's the minor key's gateway — the "ii" chord in any minor ii-V-i. Wistful and
            unresolved.
          </p>
          <p>
            <strong>Fully diminished (dim7)</strong> is a stack of three minor thirds — the
            entire chord is symmetric, which means every note can act as the root. Cdim7,
            E♭dim7, G♭dim7 and Adim7 are <em>the same four notes</em>, just respelled. That's
            why diminished chords are such powerful pivots between keys.
          </p>
        </Prose>

        <Glossary>
          <Term name="Tritone" def="Three whole-tones — the interval that gives the dominant 7 chord its tension. Six semitones." />
          <Term name="Cadence" def="A musical 'punctuation mark'. The classic V7 → I cadence is built around the dominant 7 chord resolving to the I." />
          <Term name="Symmetric chord" def="A chord whose intervals are all equal. Augmented (every major 3rd) and dim7 (every minor 3rd) are the two symmetric chord families — every inversion sounds like a new chord." />
        </Glossary>
      </Chapter>

      <Chapter eyebrow="Build all five" title="Hear them on every root">
        <Prose>
          <p>
            Use the chord builder to flip through all five seventh-chord families. Pick any
            root, click each chord, and listen for what the seventh adds. Especially worth
            comparing side-by-side:
          </p>
          <ul className="space-y-1 text-[14px]">
            <li><strong>Cmaj7 vs. C7</strong> — only one note differs (B vs B♭). The whole emotion flips.</li>
            <li><strong>Cm7 vs. Cm7♭5</strong> — only one note differs (G vs G♭). Stability vs. instability.</li>
            <li><strong>Cm7♭5 vs. Cdim7</strong> — only one note differs (B♭ vs B♭♭). The half-diminished resolves; the fully-diminished pivots.</li>
          </ul>
        </Prose>

        <ChordBuilder families={["seventh"]} />
      </Chapter>

      <Chapter eyebrow="Where each seventh lives" title="The diatonic-7 chord tree">
        <Prose>
          <p>
            Each of the five sevenths has a natural home in the major scale. If you build a
            seventh chord on every degree of C major — taking only notes from the C major
            scale — you get a full diatonic seventh chord tree:
          </p>
          <ul className="space-y-1 text-[14px]">
            <li><strong>I — Cmaj7</strong> (C E G B)</li>
            <li><strong>ii — Dm7</strong> (D F A C)</li>
            <li><strong>iii — Em7</strong> (E G B D)</li>
            <li><strong>IV — Fmaj7</strong> (F A C E)</li>
            <li><strong>V — G7</strong> (G B D F) ← the only dominant 7 in the key</li>
            <li><strong>vi — Am7</strong> (A C E G)</li>
            <li><strong>vii° — Bm7♭5</strong> (B D F A)</li>
          </ul>
          <p>
            That's why the V chord is special: it's the <em>only</em> degree of the major
            scale that produces a dominant 7. Wherever you hear a 7-with-no-suffix in a
            song ("G7", "D7"), you're hearing the V of some key — even if briefly — and the
            song will almost certainly resolve down a fifth into the I.
          </p>
        </Prose>

        <TheoryBox label="The dominant-7 rule">
          A bare "7" (e.g. G7, D7, A7) is always a dominant 7 chord — major triad with a
          flat 7. It is the V of a key. It pulls strongly toward a chord one perfect fifth
          below it (G7 → C, D7 → G, A7 → D).
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="What's next" title="Beyond seven · into the tensions">
        <Prose>
          <p>
            With four notes in our chord we've covered almost every standard pop and rock
            tune ever written. But jazz keeps stacking. The next chapter takes the seventh
            chord and goes further — adding 9ths, 11ths, 13ths, plus their flat and sharp
            alterations. That's where colour really begins.
          </p>
        </Prose>
        <NoteImage
          src="/notes/music-theory/finals/21b-all-seventh-chord-types.png"
          caption="Jason's complete 7th-chord chart. Major 7 (1357) → Cmaj7. Dominant 7 (135♭7) → C7. Minor 7 (1♭35♭7) → Cm7. Minor-Major 7 (1♭357) → Cm(maj7). Half-diminished (1♭3♭5♭7) → Cm7♭5. Fully diminished (1♭3♭5♭♭7) → Cdim7. Plus the rare 7sus4, maj7♭5, aug7. Six families do almost everything."
        />
      </Chapter>

      <NextUp
        to="/lesson/music-theory-22"
        label="Chapter 22 · Chord Formation - Jazz Extensions & Tensions"
        why="With seventh chords solid, we add 9ths, 11ths and 13ths — and the ♭/♯ alterations on each. The colour palette of jazz harmony."
      />
    </LessonShell>
  );
}
